import React from 'react';
import { Button } from '@this/shared/ui/inputs/button';
import { Checkbox } from '@this/shared/ui/inputs/checkbox/checkbox';
import { FormControl, FormHelperText } from '@this/shared/ui/inputs/form_control/form_control';
import { Input } from '@this/shared/ui/inputs/input/input';
import { Fetcher } from '@this/src/util';
import type ReserveInfo from '@this/domain/reserve_info2';
import type TravelerInformation from '@this/domain/traveler_information';

type TabiregiShareReserveInfo = Pick<ReserveInfo, 'searchQueryId' | 'reservingTrip'>;

interface TravelerOption {
  travelerKey: string;
  travelerInformationId: number | null;
  userId: number | null;
  name: string;
  email: string;
  isManualUser: boolean;
  checked: boolean;
  emailError: string | null;
}

interface Props {
  reserveInfo: TabiregiShareReserveInfo;
  onClose: () => void;
}

type State = {
  submitting: boolean;
  completed: boolean;
  error: string | null;
  travelers: TravelerOption[];
};

const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildTravelerKey = ({
  userId,
  travelerInformationId
}: {
  userId: number | null;
  travelerInformationId: number | null;
}) => {
  if (userId != null) {
    return `user-${userId}`;
  }

  if (travelerInformationId != null) {
    return `manual-${travelerInformationId}`;
  }

  return '';
};

const buildTravelerOption = (traveler: TravelerInformation): TravelerOption | null => {
  const userId = traveler.userId ?? null;
  const travelerInformationId = traveler.id ?? null;

  if (userId == null && travelerInformationId == null) {
    return null;
  }

  const isManualUser = userId == null && travelerInformationId != null;
  const name =
    traveler.name ||
    traveler.travelerName ||
    `${traveler.lastNameRoman || ''} ${traveler.firstNameRoman || ''}`.trim();
  const email = isManualUser ? '' : traveler.user?.email ?? '';

  return {
    travelerKey: buildTravelerKey({ userId, travelerInformationId }),
    travelerInformationId,
    userId,
    name,
    email,
    isManualUser,
    checked: false,
    emailError: null
  };
};

class TabiregiShareModal extends React.Component<Props, State> {
  state: State = {
    submitting: false,
    completed: false,
    error: null,
    travelers: this.props.reserveInfo.reservingTrip.travelers.reduce<TravelerOption[]>((result, traveler) => {
      const option = buildTravelerOption(traveler);

      if (option) {
        result.push(option);
      }

      return result;
    }, [])
  };

  handleCheckChange = (travelerKey: string) => {
    this.setState(prevState => ({
      error: null,
      travelers: prevState.travelers.map(traveler =>
        traveler.travelerKey === travelerKey
          ? {
              ...traveler,
              checked: !traveler.checked,
              emailError: traveler.checked ? null : traveler.emailError
            }
          : traveler
      )
    }));
  };

  handleEmailChange = (travelerKey: string, email: string) => {
    this.setState(prevState => ({
      travelers: prevState.travelers.map(traveler =>
        traveler.travelerKey === travelerKey ? { ...traveler, email, emailError: null } : traveler
      )
    }));
  };

  validateTravelers = (travelers: TravelerOption[]) => {
    let hasError = false;

    const nextTravelers = travelers.map(traveler => {
      if (!traveler.checked || !traveler.isManualUser) {
        return { ...traveler, emailError: null };
      }

      const trimmedEmail = traveler.email.trim();
      if (!trimmedEmail) {
        hasError = true;
        return { ...traveler, emailError: 'メールアドレスを入力してください。' };
      }

      if (!EMAIL_REGEXP.test(trimmedEmail)) {
        hasError = true;
        return { ...traveler, emailError: 'メールアドレスの形式が正しくありません。' };
      }

      return { ...traveler, email: trimmedEmail, emailError: null };
    });

    return { hasError, travelers: nextTravelers };
  };

  handleShare = async () => {
    const { reserveInfo } = this.props;
    const { travelers } = this.state;
    const validated = this.validateTravelers(travelers);
    const selectedUserIds = validated.travelers
      .filter(traveler => traveler.checked && !traveler.isManualUser && traveler.userId != null)
      .map(traveler => traveler.userId as number);
    const manualRecipients = validated.travelers
      .filter(traveler => traveler.checked && traveler.isManualUser && traveler.travelerInformationId != null)
      .map(traveler => ({
        traveler_information_id: traveler.travelerInformationId as number,
        email: traveler.email
      }));

    if (selectedUserIds.length === 0 && manualRecipients.length === 0) {
      this.setState({ error: '共有先のユーザーを選択してください。', travelers: validated.travelers });
      return;
    }

    if (validated.hasError) {
      this.setState({ travelers: validated.travelers });
      return;
    }

    this.setState({ submitting: true, error: null, travelers: validated.travelers });

    try {
      await Fetcher.post('/tabiregi_consents/share.json', {
        search_query_id: reserveInfo.searchQueryId,
        user_ids: selectedUserIds,
        manual_recipients: manualRecipients
      });
      this.setState({ completed: true });
    } catch {
      this.setState({ error: '共有に失敗しました。もう一度お試しください。' });
    } finally {
      this.setState({ submitting: false });
    }
  };

  renderTravelerLabel = (traveler: TravelerOption) => {
    if (traveler.isManualUser && !traveler.email) {
      return traveler.name;
    }

    return `${traveler.name}(${traveler.email})`;
  };

  render() {
    const { onClose } = this.props;
    const { submitting, completed, error, travelers } = this.state;
    const styles = getStyles();

    return (
      <div style={styles.overlay} onClick={onClose} data-testid="tabiregi-share-modal-overlay">
        <div style={styles.modal} onClick={e => e.stopPropagation()} data-testid="tabiregi-share-modal">
          <h3 style={styles.modalTitle}>たびレジ共有フォーム</h3>
          {completed ? (
            <div style={styles.modalBody}>
              <p style={styles.successMessage} data-testid="tabiregi-share-success">
                選択したユーザーにたびレジ登録案内を送信しました。
              </p>
              <div style={styles.buttonArea}>
                <Button onClick={onClose}>閉じる</Button>
              </div>
            </div>
          ) : (
            <div style={styles.modalBody}>
              <p style={styles.description}>下記ユーザにたびレジ登録を教えます。</p>
              <div style={styles.travelerList} data-testid="tabiregi-share-traveler-list">
                {travelers.map(traveler => (
                  <div key={traveler.travelerKey} style={styles.travelerItem}>
                    <div style={styles.travelerRow}>
                      <Checkbox
                        checked={traveler.checked}
                        onChange={() => this.handleCheckChange(traveler.travelerKey)}
                        data-testid={`tabiregi-share-checkbox-${traveler.travelerKey}`}
                      >
                        <span style={styles.travelerName}>{this.renderTravelerLabel(traveler)}</span>
                      </Checkbox>
                    </div>
                    {traveler.checked && traveler.isManualUser && (
                      <div style={styles.emailFieldArea}>
                        <FormControl error={!!traveler.emailError} style={styles.emailFormControl}>
                          <Input
                            value={traveler.email}
                            onChange={event => this.handleEmailChange(traveler.travelerKey, event.target.value)}
                            placeholder="メールアドレスを入力してください"
                            type="email"
                            data-testid={`tabiregi-share-email-${traveler.travelerKey}`}
                          />
                          {traveler.emailError && <FormHelperText>{traveler.emailError}</FormHelperText>}
                        </FormControl>
                      </div>
                    )}
                  </div>
                ))}
                {travelers.length === 0 && <p style={styles.emptyMessage}>共有可能なユーザーがいません。</p>}
              </div>
              {error && (
                <p style={styles.errorMessage} data-testid="tabiregi-share-error">
                  {error}
                </p>
              )}
              <div style={styles.buttonArea}>
                <Button color="sub" onClick={onClose} disabled={submitting}>
                  戻る
                </Button>
                <Button
                  onClick={this.handleShare}
                  loading={submitting}
                  disabled={submitting || travelers.length === 0}
                  data-testid="tabiregi-share-submit"
                >
                  教える
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

function getStyles() {
  return {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    } as React.CSSProperties,
    modal: {
      background: '#fff',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '500px',
      width: '90%',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    } as React.CSSProperties,
    modalTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '1px solid #ddd'
    } as React.CSSProperties,
    modalBody: {
      fontSize: '14px',
      lineHeight: 1.6
    } as React.CSSProperties,
    description: {
      marginBottom: '16px'
    } as React.CSSProperties,
    travelerList: {
      marginBottom: '16px'
    } as React.CSSProperties,
    travelerItem: {
      padding: '8px 0',
      borderBottom: '1px solid #eee'
    } as React.CSSProperties,
    travelerRow: {
      display: 'flex',
      alignItems: 'center'
    } as React.CSSProperties,
    emailFieldArea: {
      paddingLeft: '28px',
      paddingTop: '8px'
    } as React.CSSProperties,
    emailFormControl: {
      width: '100%'
    } as React.CSSProperties,
    travelerName: {
      fontSize: '14px'
    } as React.CSSProperties,
    emptyMessage: {
      color: '#999',
      fontSize: '13px',
      padding: '8px 0'
    } as React.CSSProperties,
    successMessage: {
      marginBottom: '20px',
      color: '#28a745',
      fontWeight: 'bold'
    } as React.CSSProperties,
    errorMessage: {
      marginBottom: '12px',
      color: '#dc3545',
      fontSize: '13px'
    } as React.CSSProperties,
    buttonArea: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '16px'
    } as React.CSSProperties
  } as const;
}

export default TabiregiShareModal;
